import { DndContext, DragOverlay, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import type { CollisionDetection, DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ChatBubble,
  ChatBubbleOutline,
  Check,
  Close,
  CloudDone,
  CloudOutlined,
  CloudSync,
  ControlPoint,
  Delete,
  Edit,
  ExpandLess,
  ExpandMore,
  FilterList,
  GridOn,
  GridView,
  PushPin,
  PushPinOutlined,
  Reorder,
  StickyNote2,
  StickyNote2Outlined,
  TableRows,
  ViewStream,
} from '@mui/icons-material'
import {
  Badge,
  Box,
  CircularProgress,
  IconButton,
  Popover,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from '@mui/material'
import type { ChapterSectionDependency } from '@shared/chapterSectionDependencies'
import { chapterSectionKey } from '@shared/chapterSectionsOrder'
import { DISCUSSED_TAG_ID } from '@shared/tags/canonicalDiscussedTag'
import { PINNED_TAG_ID } from '@shared/tags/canonicalPinnedTag'
import { type SectionRatings, splitSectionTags } from '@shared/tags/canonicalRatingTags'
import type { TagDefinition } from '@shared/tags/tagRegistry'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import ChapterNotePopover from './ChapterNotePopover'
import SectionDependencyChip from './dependencies/SectionDependencyChip'
import DroppableContainer from './organize-chapters/DroppableContainer'
import OrganizeChaptersDialog from './organize-chapters/OrganizeChaptersDialog'
import ElementChapterMatrixDialog from './organize-elements/ElementChapterMatrixDialog'
import ElementsBoardDialog from './organize-elements/ElementsBoardDialog'
import SectionElementMatrixDialog from './organize-elements/SectionElementMatrixDialog'
import OrganizeFlatSectionsDialog from './OrganizeFlatSectionsDialog'
import {
  SectionRatingFilterPanel,
  rowMatchesRatingFilter,
  sectionPrimaryGroup,
  useSectionRatingFilter,
} from './sectionRatingFilter'
import { SectionTagEditor } from '../../../components/tags/SectionTagEditor'
import { SectionTagRail } from '../../../components/tags/SectionTagRail'
import TagManagerDialog from '../../../components/tags/TagManagerDialog'
import { useSectionTagsEditing } from '../../../components/tags/useSectionTagsEditing'
import { Book, ContentFile } from '../../../services/api'
import { logger } from '../../../utils/logger'
import type { ChapterGroup, ChapterSectionEntry } from '../hooks/useChapterSectionsBook'
import type { SectionDependencyView } from '../hooks/useSectionDependencies'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ChapterSectionsTocProps {
  chapterGroups: ChapterGroup[]
  chapterSectionEntries: ChapterSectionEntry[]
  book: Book | null
  bookId: string | undefined
  currentChapter: ContentFile | null
  activeHeadingId?: string | null
  /** ChapterSection key (`filename:headingBlockId`) representing where the cursor currently is. */
  currentSectionKey?: string | null
  isAuthor: boolean
  readOnly: boolean
  isMobile: boolean
  chapterSectionPublishBusyKey: string | null
  entryByKey: Map<string, ChapterSectionEntry>
  tagRegistry?: Record<string, TagDefinition>
  tagColorMap?: Record<string, string>
  onHeadingNavigation: (chapter: ContentFile, headingBlockId: string) => void
  onChapterSectionPublish: (chapter: ContentFile, headingBlockId: string, ev: React.MouseEvent) => void
  onSectionDragEnd: (
    chapterDefs: Array<{ id: string; title: string; chapterSections: string[] }>,
    deps?: ChapterSectionDependency[],
  ) => Promise<void>
  /** Persists `book.settings.flatSectionsOrder`. Called from OrganizeFlatSectionsDialog. */
  onFlatSectionsOrderChange?: (next: string[]) => Promise<void>
  /** Shared dependency view — same data in chapters and flat mode. */
  sectionDependencies?: SectionDependencyView
  onEditDependency?: (dep: ChapterSectionDependency) => void
  onRemoveDependency?: (depId: string) => Promise<void>
  /** Opens the dependency editor prefilled with a picked pair (enables the organizer's link mode). */
  onCreateDependency?: (fromKey: string, toKey: string) => void
  onRenameChapter: (chapterId: string, newTitle: string) => void
  /** Saves the chapter's author-only working note (empty text clears it). */
  onSetChapterNote: (chapterId: string, note: string) => Promise<void>
  onDeleteChapter: (chapterId: string) => void
  /** Inserts an empty chapter directly before `chapterId`; resolves with the new chapter's id. */
  onAddChapterBefore: (chapterId: string) => Promise<string | undefined>
  onSectionTagsPersisted?: () => void
  onRegistrySaved?: (tags: Record<string, TagDefinition>) => void
  /** Per-element ordered section keys + persist handler (forwarded to the elements board). */
  elementSectionOrder?: Record<string, string[]>
  onElementSectionOrderChange?: (order: Record<string, string[]>) => Promise<void>
  /** Forwarded to OrganizeChaptersDialog; fires after the section-preview editor saves. */
  onSectionContentSaved?: (filename: string, updatedFile: ContentFile) => void
  /** Opens the continuous all-placed-sections editing view. Shown in the Sections (flat) view. */
  onOpenContinuousView?: () => void
}

// ---------------------------------------------------------------------------
// ChapterGroupHeader
// ---------------------------------------------------------------------------

function ChapterGroupHeader({
  chapterId: _chapterId,
  title,
  sectionCount,
  collapsed,
  onToggle,
  isAuthor,
  readOnly,
  onRename,
  onDelete,
  onAddBefore,
  note,
  onOpenNote,
  isRtl,
  autoEdit,
  onAutoEditEnd,
  fontFamily,
}: {
  chapterId: string
  title: string
  sectionCount: number
  collapsed: boolean
  onToggle: () => void
  isAuthor: boolean
  readOnly: boolean
  onRename: (newTitle: string) => void
  onDelete: () => void
  onAddBefore: () => void
  /** The chapter's working note — drives the icon's state and its tooltip. */
  note?: string
  onOpenNote: (ev: React.MouseEvent) => void
  isRtl: boolean
  /** A chapter just created from this header opens straight into its title field. */
  autoEdit?: boolean
  onAutoEditEnd?: () => void
  fontFamily?: string
}) {
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(title)
  const inputRef = useRef<HTMLInputElement>(null)

  // The chapter is persisted before this header mounts, so auto-edit arrives as a
  // prop change rather than an initial value.
  useEffect(() => {
    if (!autoEdit) {
      return
    }
    setEditValue(title)
    setEditing(true)
    const timer = setTimeout(() => inputRef.current?.focus(), 50)
    return () => clearTimeout(timer)
    // Keyed on autoEdit alone: a later title change must not reopen the field.
  }, [autoEdit])

  const handleStartEdit = () => {
    setEditValue(title)
    setEditing(true)
    setTimeout(() => inputRef.current?.focus(), 50)
  }

  const handleConfirmEdit = () => {
    setEditing(false)
    onAutoEditEnd?.()
    const trimmed = editValue.trim()
    if (trimmed && trimmed !== title) {
      onRename(trimmed)
    }
  }

  const handleCancelEdit = () => {
    setEditing(false)
    setEditValue(title)
    onAutoEditEnd?.()
  }

  const canEdit = isAuthor && !readOnly

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        px: 0.5,
        pt: 0.75,
        pb: 0.25,
        borderRadius: 1,
        '&:hover .chapter-actions': { opacity: 1 },
        '&:hover .chapter-note-off': { opacity: 0.55 },
      }}
    >
      <Box onClick={onToggle} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {collapsed ? (
          <ExpandMore sx={{ fontSize: 18, color: 'var(--sidebar-text)', opacity: 0.7 }} />
        ) : (
          <ExpandLess sx={{ fontSize: 18, color: 'var(--sidebar-text)', opacity: 0.7 }} />
        )}
      </Box>

      {editing ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
          <TextField
            inputRef={inputRef}
            size="small"
            variant="standard"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleConfirmEdit()
              }
              if (e.key === 'Escape') {
                handleCancelEdit()
              }
            }}
            onBlur={handleConfirmEdit}
            sx={{
              flex: 1,
              '& .MuiInput-input': {
                fontSize: '0.95rem',
                fontWeight: 700,
                py: 0,
                color: 'var(--sidebar-text)',
              },
            }}
          />
          <IconButton size="small" onClick={handleConfirmEdit} sx={{ p: 0.25 }}>
            <Check sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton size="small" onClick={handleCancelEdit} sx={{ p: 0.25 }}>
            <Close sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      ) : (
        <Box
          onClick={onToggle}
          sx={{
            flex: 1,
            minWidth: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: fontFamily || 'SBL Hebrew',
              fontWeight: 700,
              color: 'var(--sidebar-text)',
              fontSize: '0.95rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title || 'Untitled Chapter'}
          </Typography>
          {collapsed && (
            <Typography
              component="span"
              sx={{ ml: 0.75, fontSize: '0.68rem', color: 'var(--sidebar-text)', opacity: 0.5 }}
            >
              ({sectionCount})
            </Typography>
          )}
        </Box>
      )}

      {canEdit && !editing && (
        <>
          <IconButton
            size="small"
            className={note ? undefined : 'chapter-note-off'}
            // The header row toggles collapse on click and can be dragged to reorder —
            // keep both from firing when the author reaches for the note.
            onPointerDown={(ev) => ev.stopPropagation()}
            onMouseDown={(ev) => ev.stopPropagation()}
            onClick={(ev) => {
              ev.stopPropagation()
              onOpenNote(ev)
            }}
            sx={{
              flexShrink: 0,
              p: 0.25,
              opacity: note ? 0.75 : 0,
              transition: 'opacity 0.15s',
              '&&:hover': { opacity: 1, bgcolor: 'var(--bg-hover)' },
            }}
            title={note || (isRtl ? 'הערות על הפרק' : 'Notes on this chapter')}
          >
            {note ? (
              <StickyNote2 sx={{ fontSize: 14, color: 'var(--sidebar-text)', opacity: 0.8 }} />
            ) : (
              <StickyNote2Outlined sx={{ fontSize: 14, color: 'var(--sidebar-text)', opacity: 0.6 }} />
            )}
          </IconButton>
          <Box className="chapter-actions" sx={{ display: 'flex', opacity: 0, transition: 'opacity 0.15s' }}>
            <IconButton size="small" onClick={handleStartEdit} sx={{ p: 0.25 }} title="Rename chapter">
              <Edit sx={{ fontSize: 14, color: 'var(--sidebar-text)', opacity: 0.6 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                const msg =
                  sectionCount > 0
                    ? `Delete "${title || 'Untitled Chapter'}"? Its ${sectionCount} section(s) will move to the adjacent chapter.`
                    : `Delete "${title || 'Untitled Chapter'}"?`
                if (window.confirm(msg)) {
                  onDelete()
                }
              }}
              sx={{ p: 0.25 }}
              title="Delete chapter"
            >
              <Delete sx={{ fontSize: 14, color: 'var(--sidebar-text)', opacity: 0.6 }} />
            </IconButton>
            <IconButton size="small" onClick={onAddBefore} sx={{ p: 0.25 }} title="Add chapter above">
              <ControlPoint sx={{ fontSize: 14, color: 'var(--sidebar-text)', opacity: 0.6 }} />
            </IconButton>
          </Box>
        </>
      )}
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Sortable wrapper for flat-mode rows
// ---------------------------------------------------------------------------

const FLAT_PLACED_ID = '__toc_flat_placed__'
const FLAT_UNPLACED_ID = '__toc_flat_unplaced__'

function SortableTocRow({ id, disabled, children }: { id: string; disabled?: boolean; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled })
  // touchAction: 'manipulation' (not 'none') so the browser handles vertical
  // scrolling of the sidebar — the TouchSensor's delay activation already
  // distinguishes scroll from long-press, and 'none' silently broke scroll on
  // mobile by telling the browser to ignore the touch entirely.
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    touchAction: 'manipulation',
  }
  const dragProps = disabled ? {} : { ...attributes, ...listeners }
  return (
    <div ref={setNodeRef} style={style} data-section-key={id} {...dragProps}>
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// ChapterSectionsToc — main component
// ---------------------------------------------------------------------------

export default function ChapterSectionsToc({
  chapterGroups,
  chapterSectionEntries,
  book,
  bookId,
  currentChapter,
  activeHeadingId,
  currentSectionKey,
  isAuthor,
  readOnly,
  isMobile: _isMobile,
  chapterSectionPublishBusyKey,
  entryByKey,
  tagRegistry,
  tagColorMap,
  onHeadingNavigation,
  onChapterSectionPublish,
  onSectionDragEnd,
  onFlatSectionsOrderChange,
  sectionDependencies,
  onEditDependency,
  onRemoveDependency,
  onCreateDependency,
  onRenameChapter,
  onSetChapterNote,
  onDeleteChapter,
  onAddChapterBefore,
  onSectionTagsPersisted,
  onRegistrySaved,
  elementSectionOrder,
  onElementSectionOrderChange,
  onSectionContentSaved,
  onOpenContinuousView,
}: ChapterSectionsTocProps) {
  const multipleChapters = chapterGroups.length > 1

  /** Chapter created from a header's add button — its title field opens as soon as it renders. */
  const [justCreatedChapterId, setJustCreatedChapterId] = useState<string | null>(null)

  const handleAddChapterBefore = useCallback(
    async (chapterId: string) => {
      const newId = await onAddChapterBefore(chapterId)
      setJustCreatedChapterId(newId ?? null)
    },
    [onAddChapterBefore],
  )
  const [collapsedChapters, setCollapsedChapters] = useState<Set<string>>(new Set())
  const [organizeDialogOpen, setOrganizeDialogOpen] = useState(false)
  const [elementsBoardOpen, setElementsBoardOpen] = useState(false)
  const [matrixOpen, setMatrixOpen] = useState(false)
  const [sectionMatrixOpen, setSectionMatrixOpen] = useState(false)
  const [organizeFlatDialogOpen, setOrganizeFlatDialogOpen] = useState(false)
  const [tagPopover, setTagPopover] = useState<{ key: string; top: number; left: number } | null>(null)
  const [notePopover, setNotePopover] = useState<{ chapterId: string; top: number; left: number } | null>(null)
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null)
  const [pickerSectionKey, setPickerSectionKey] = useState<string | null>(null)

  // ── Editor sidebar tab: Chapters | Sections ──
  //
  // First-load default tracks the book setting; thereafter the user's choice
  // sticks in localStorage per book. Independent of the book's published
  // structure so the author can review either view at any time.
  const tabStorageKey = bookId ? `editor-toc-view:${bookId}` : null
  const [viewMode, setViewMode] = useState<'chapters' | 'flat'>(() => {
    if (tabStorageKey) {
      const stored = localStorage.getItem(tabStorageKey)
      if (stored === 'chapters' || stored === 'flat') {
        return stored
      }
    }
    return book?.settings.structure === 'flat' ? 'flat' : 'chapters'
  })
  useEffect(() => {
    if (tabStorageKey) {
      localStorage.setItem(tabStorageKey, viewMode)
    }
  }, [tabStorageKey, viewMode])

  const flatOrder = useMemo(() => book?.settings.flatSectionsOrder ?? [], [book?.settings.flatSectionsOrder])
  const allCanonicalKeys = useMemo(() => Array.from(entryByKey.keys()), [entryByKey])
  const flatUnplacedFromProps = useMemo(() => {
    const ordered = new Set(flatOrder)
    return allCanonicalKeys.filter((k) => !ordered.has(k))
  }, [flatOrder, allCanonicalKeys])
  const flatViewAvailable = book?.settings.flatSectionsOrder !== undefined

  // ── Flat-view drag-and-drop (TOC) ──
  //
  // The TOC mirrors OrganizeFlatSectionsDialog's two-zone model: Placed
  // (the persisted `flatSectionsOrder`) and Unplaced (everything else, in
  // chapter-walk order). Drag-and-drop is enabled only in the Sections view and
  // only when the author can persist changes.
  const flatDragEnabled = isAuthor && !readOnly && Boolean(onFlatSectionsOrderChange)
  const [draftFlatOrder, setDraftFlatOrder] = useState<string[]>(flatOrder)
  const [draftFlatUnplaced, setDraftFlatUnplaced] = useState<string[]>(flatUnplacedFromProps)
  const [activeFlatDragId, setActiveFlatDragId] = useState<string | null>(null)

  // Sync local draft from props when not actively dragging. During a drag, the
  // local state owns the visual layout so cross-zone moves animate smoothly.
  useEffect(() => {
    if (!activeFlatDragId) {
      setDraftFlatOrder(flatOrder)
      setDraftFlatUnplaced(flatUnplacedFromProps)
    }
  }, [flatOrder, flatUnplacedFromProps, activeFlatDragId])

  const flatSensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    // 300ms long-press on touch — short enough to feel responsive, long enough
    // that a swipe-to-scroll never accidentally arms a drag.
    useSensor(TouchSensor, { activationConstraint: { delay: 300, tolerance: 5 } }),
  )

  const flatContainerOf = useCallback(
    (id: string): typeof FLAT_PLACED_ID | typeof FLAT_UNPLACED_ID | null => {
      if (id === FLAT_PLACED_ID) {
        return FLAT_PLACED_ID
      }
      if (id === FLAT_UNPLACED_ID) {
        return FLAT_UNPLACED_ID
      }
      if (draftFlatOrder.includes(id)) {
        return FLAT_PLACED_ID
      }
      if (draftFlatUnplaced.includes(id)) {
        return FLAT_UNPLACED_ID
      }
      return null
    },
    [draftFlatOrder, draftFlatUnplaced],
  )

  const detectFlatCollisions: CollisionDetection = useCallback((args) => closestCenter(args), [])

  const handleFlatDragStart = useCallback((ev: DragStartEvent) => {
    setActiveFlatDragId(ev.active.id as string)
  }, [])

  const handleFlatDragOver = useCallback(
    (ev: DragOverEvent) => {
      const { active, over } = ev
      if (!over) {
        return
      }
      const activeId = active.id as string
      const overId = over.id as string
      const activeContainer = flatContainerOf(activeId)
      const overContainer = flatContainerOf(overId)
      if (!activeContainer || !overContainer || activeContainer === overContainer) {
        return
      }
      if (activeContainer === FLAT_PLACED_ID && overContainer === FLAT_UNPLACED_ID) {
        setDraftFlatOrder((prev) => prev.filter((k) => k !== activeId))
        setDraftFlatUnplaced((prev) => {
          if (prev.includes(activeId)) {
            return prev
          }
          const targetIdx = overId === FLAT_UNPLACED_ID ? prev.length : prev.indexOf(overId)
          const insertAt = targetIdx === -1 ? prev.length : targetIdx
          const next = [...prev]
          next.splice(insertAt, 0, activeId)
          return next
        })
      } else if (activeContainer === FLAT_UNPLACED_ID && overContainer === FLAT_PLACED_ID) {
        setDraftFlatUnplaced((prev) => prev.filter((k) => k !== activeId))
        setDraftFlatOrder((prev) => {
          if (prev.includes(activeId)) {
            return prev
          }
          const targetIdx = overId === FLAT_PLACED_ID ? prev.length : prev.indexOf(overId)
          const insertAt = targetIdx === -1 ? prev.length : targetIdx
          const next = [...prev]
          next.splice(insertAt, 0, activeId)
          return next
        })
      }
    },
    [flatContainerOf],
  )

  const handleFlatDragEnd = useCallback(
    (ev: DragEndEvent) => {
      const { active, over } = ev
      setActiveFlatDragId(null)
      if (!over) {
        return
      }
      const activeId = active.id as string
      const overId = over.id as string

      // Compute the final Placed order after this drag.
      let nextOrder = draftFlatOrder
      const activeContainer = flatContainerOf(activeId)
      const overContainer = flatContainerOf(overId)

      // Within-Placed reorder.
      if (activeId !== overId && activeContainer === FLAT_PLACED_ID && overContainer === FLAT_PLACED_ID) {
        const oldIdx = draftFlatOrder.indexOf(activeId)
        const newIdx = draftFlatOrder.indexOf(overId)
        if (oldIdx !== -1 && newIdx !== -1) {
          nextOrder = arrayMove(draftFlatOrder, oldIdx, newIdx)
          setDraftFlatOrder(nextOrder)
        }
      }
      // Cross-zone moves are already applied to draftFlatOrder by handleFlatDragOver;
      // we just persist whatever the current draft is.

      // Persist if the order actually changed.
      const changed = nextOrder.length !== flatOrder.length || nextOrder.some((k, i) => k !== flatOrder[i])
      if (changed && onFlatSectionsOrderChange) {
        void onFlatSectionsOrderChange(nextOrder).catch((err) => {
          logger.warn('Failed to persist flat sections order from TOC', err)
        })
      }
    },
    [draftFlatOrder, flatContainerOf, flatOrder, onFlatSectionsOrderChange],
  )

  const flatPlacedEntries = useMemo(
    () => draftFlatOrder.map((k) => entryByKey.get(k)).filter((e): e is ChapterSectionEntry => e != null),
    [draftFlatOrder, entryByKey],
  )
  const flatUnplacedEntries = useMemo(
    () => draftFlatUnplaced.map((k) => entryByKey.get(k)).filter((e): e is ChapterSectionEntry => e != null),
    [draftFlatUnplaced, entryByKey],
  )

  const activeFlatEntry = activeFlatDragId ? entryByKey.get(activeFlatDragId) : null

  // Center the active section in its TOC scroll container the first time it mounts —
  // mainly so the mobile drawer (which remounts on every open) shows the active row.
  const initialScrollDone = useRef(false)
  const scrollActiveRowIntoView = useCallback((node: HTMLElement | null) => {
    if (!node || initialScrollDone.current) {
      return
    }
    initialScrollDone.current = true
    requestAnimationFrame(() => {
      let scrollParent: HTMLElement | null = node.parentElement
      while (scrollParent) {
        const style = getComputedStyle(scrollParent)
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          break
        }
        scrollParent = scrollParent.parentElement
      }
      if (!scrollParent) {
        node.scrollIntoView({ block: 'center', behavior: 'instant' })
        return
      }
      const parentRect = scrollParent.getBoundingClientRect()
      const nodeRect = node.getBoundingClientRect()
      const targetTop = parentRect.top + parentRect.height / 2 - nodeRect.height / 2
      const offset = nodeRect.top - targetTop
      scrollParent.scrollBy({ top: offset, behavior: 'instant' })
    })
  }, [])

  const { effectiveTagIds, changeTagIds } = useSectionTagsEditing({
    bookId,
    entryByKey,
    onPersisted: onSectionTagsPersisted,
  })

  const canEditTags = isAuthor && !readOnly && Boolean(tagRegistry)

  const filterApi = useSectionRatingFilter({
    entries: chapterSectionEntries,
    tagRegistry,
    effectiveTagIds,
  })

  const handleJumpToGroup = useCallback(
    (label: string) => {
      const entry = filterApi.firstEntryByGroup.get(label)
      if (!entry) {
        return
      }
      setFilterAnchor(null)
      onHeadingNavigation(entry.chapter, entry.headingBlockId)
    },
    [filterApi.firstEntryByGroup, onHeadingNavigation],
  )

  const toggleCollapse = useCallback((chapterId: string) => {
    setCollapsedChapters((prev) => {
      const next = new Set(prev)
      if (next.has(chapterId)) {
        next.delete(chapterId)
      } else {
        next.add(chapterId)
      }
      return next
    })
  }, [])

  // ── Render a single section row ──

  const renderSectionRow = (row: ChapterSectionEntry) => {
    const sectionPublished = Boolean(row.chapter.publishedSections?.includes(row.headingBlockId))
    // Published, but an encrypted passage inside it has no glyphs: readers are being
    // served an empty space where the text used to be, until this is re-published.
    const sectionNeedsRepublish = sectionPublished && row.needsRepublish
    const rowSelected = currentChapter?.filename === row.chapter.filename
    const sectionIsActive = rowSelected && activeHeadingId === row.headingBlockId
    const actionKey = `${row.chapter.filename}:${row.headingBlockId}`
    const busy = chapterSectionPublishBusyKey === actionKey
    const pathLine = [row.chapter.title, ...row.ancestorLabels]
      .map((s) => s.trim())
      .filter(Boolean)
      .join(' · ')

    const sectionKey = chapterSectionKey(row.chapter.filename, row.headingBlockId)
    const tagIds = tagRegistry ? effectiveTagIds(sectionKey) : []
    const requestEditTags = canEditTags
      ? (ev: React.MouseEvent) => setTagPopover({ key: sectionKey, top: ev.clientY, left: ev.clientX })
      : undefined
    const rowRatings = tagRegistry ? splitSectionTags(tagIds, tagRegistry).ratings : ({} as SectionRatings)
    const rowDimmed =
      filterApi.isActive && !rowMatchesRatingFilter(rowRatings, sectionPrimaryGroup(row), filterApi.filter)
    const rowDiscussed = tagIds.includes(DISCUSSED_TAG_ID)
    const toggleDiscussed = () => {
      const next = rowDiscussed ? tagIds.filter((id) => id !== DISCUSSED_TAG_ID) : [...tagIds, DISCUSSED_TAG_ID]
      void changeTagIds(sectionKey, next).catch((err) => {
        logger.warn('Failed to toggle section discussion marker from TOC', err)
      })
    }
    const rowPinned = tagIds.includes(PINNED_TAG_ID)
    const togglePin = () => {
      const next = rowPinned ? tagIds.filter((id) => id !== PINNED_TAG_ID) : [...tagIds, PINNED_TAG_ID]
      void changeTagIds(sectionKey, next).catch((err) => {
        logger.warn('Failed to toggle section pin from TOC', err)
      })
    }

    return (
      <Box
        ref={sectionIsActive ? scrollActiveRowIntoView : undefined}
        onContextMenu={
          canEditTags
            ? (ev) => {
                ev.preventDefault()
                ev.stopPropagation()
                setTagPopover({ key: sectionKey, top: ev.clientY, left: ev.clientX })
              }
            : undefined
        }
        sx={(theme) => {
          const activeBg =
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.common.white, 0.22)
              : alpha(theme.palette.primary.main, 0.22)
          const baseBg = sectionIsActive ? activeBg : rowSelected ? 'var(--sidebar-selected)' : 'transparent'
          return {
            px: multipleChapters ? 1 : 2,
            py: 0.35,
            borderRadius: 2,
            bgcolor: baseBg,
            opacity: rowDimmed ? 0.35 : 1,
            transition: 'background-color 0.15s ease, opacity 0.15s ease',
            '&:hover': {
              bgcolor: sectionIsActive ? activeBg : rowSelected ? 'var(--sidebar-selected)' : 'var(--bg-hover)',
              opacity: rowDimmed ? 0.7 : 1,
            },
            // Unpinned pins stay faintly visible so the affordance is discoverable;
            // hovering the row brings them up, hovering the pin itself makes it solid.
            '&:hover .section-pin-off': { opacity: 0.55 },
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.2, minWidth: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            {isAuthor && (
              <IconButton
                size="small"
                disabled={busy || readOnly}
                // Stop pointer/mouse down so dnd-kit's drag listeners on the
                // wrapping sortable row don't arm when the author taps the cloud.
                onPointerDown={(ev) => ev.stopPropagation()}
                onMouseDown={(ev) => ev.stopPropagation()}
                onClick={(ev) => {
                  ev.stopPropagation()
                  void onChapterSectionPublish(row.chapter, row.headingBlockId, ev)
                }}
                sx={{
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 'calc(0.875rem * 1.4)',
                  minHeight: 'calc(0.875rem * 1.4)',
                  width: 28,
                  minWidth: 28,
                  px: 0.25,
                  py: 0,
                  borderRadius: 1,
                  color: sectionNeedsRepublish ? 'var(--encrypt-marked-border)' : 'var(--icon-default)',
                  '&:hover': {
                    color: sectionNeedsRepublish ? 'var(--encrypt-marked-hover-border)' : 'var(--icon-hover)',
                    bgcolor: 'var(--bg-hover)',
                  },
                  transition: 'opacity 0.2s ease',
                  ...(readOnly && { opacity: 0.35 }),
                }}
                title={
                  readOnly
                    ? undefined
                    : busy
                      ? 'Working…'
                      : sectionNeedsRepublish
                        ? 'Readers see a blank where an encrypted passage is — Shift+click to re-publish and mint its glyphs'
                        : sectionPublished
                          ? 'Published — click to unpublish • Shift+click to re-publish'
                          : 'Publish this chapter section'
                }
              >
                {busy ? (
                  <CircularProgress size={14} thickness={5} sx={{ color: 'var(--icon-default)' }} />
                ) : sectionNeedsRepublish ? (
                  <CloudSync fontSize="small" />
                ) : sectionPublished ? (
                  <CloudDone fontSize="small" />
                ) : (
                  <CloudOutlined fontSize="small" />
                )}
              </IconButton>
            )}
            {tagRegistry && isAuthor && (rowDiscussed || canEditTags) && (
              <IconButton
                size="small"
                disabled={readOnly}
                aria-label="דיברנו עליו"
                aria-pressed={rowDiscussed}
                title={rowDiscussed ? 'דיברנו עליו — לחיצה לביטול הסימון' : 'סמן שדיברנו עליו'}
                onPointerDown={(ev) => ev.stopPropagation()}
                onMouseDown={(ev) => ev.stopPropagation()}
                onClick={(ev) => {
                  ev.stopPropagation()
                  toggleDiscussed()
                }}
                sx={{
                  flexShrink: 0,
                  p: 0.25,
                  opacity: rowDiscussed ? 0.9 : 0.28,
                  color: rowDiscussed ? (tagRegistry[DISCUSSED_TAG_ID]?.color ?? '#64B5F6') : 'var(--sidebar-text)',
                  transition: 'opacity 0.15s ease',
                  '&&:hover': { opacity: 1, bgcolor: 'var(--bg-hover)' },
                }}
              >
                {rowDiscussed ? <ChatBubble sx={{ fontSize: 11 }} /> : <ChatBubbleOutline sx={{ fontSize: 11 }} />}
              </IconButton>
            )}
            {/* Pin sits beside the cloud so both row actions share one column,
                leaving the title free to use the full remaining width. */}
            {tagRegistry && isAuthor && (rowPinned || canEditTags) && (
              <IconButton
                size="small"
                className={rowPinned ? undefined : 'section-pin-off'}
                disabled={readOnly}
                // Same as the cloud button: keep dnd-kit's drag listeners on the
                // sortable row from arming when the author taps the pin.
                onPointerDown={(ev) => ev.stopPropagation()}
                onMouseDown={(ev) => ev.stopPropagation()}
                onClick={(ev) => {
                  ev.stopPropagation()
                  togglePin()
                }}
                sx={{
                  flexShrink: 0,
                  p: 0.25,
                  opacity: rowPinned ? 0.9 : 0.28,
                  color: rowPinned
                    ? (tagRegistry[PINNED_TAG_ID]?.color ?? 'var(--sidebar-text)')
                    : 'var(--sidebar-text)',
                  transition: 'opacity 0.15s ease',
                  '&&:hover': { opacity: 1, bgcolor: 'var(--bg-hover)' },
                }}
                title={rowPinned ? 'מקובע — המיקום ברצף סגור. לחיצה לביטול' : 'קבע את המיקום ברצף'}
              >
                {rowPinned ? <PushPin sx={{ fontSize: 11 }} /> : <PushPinOutlined sx={{ fontSize: 11 }} />}
              </IconButton>
            )}
            <Box
              onClick={() => onHeadingNavigation(row.chapter, row.headingBlockId)}
              sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
            >
              <Typography
                sx={{
                  fontFamily: book?.settings.titleFontFamily || 'SBL Hebrew',
                  fontWeight: sectionIsActive ? 700 : 400,
                  color: rowSelected ? 'var(--sidebar-selected-text)' : 'var(--sidebar-text)',
                  fontSize: '11.5px',
                  lineHeight: 1.4,
                  opacity: rowSelected ? 1 : 0.85,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {row.displayText}
              </Typography>
            </Box>
            {tagRegistry && (
              <SectionTagRail
                tagIds={tagIds}
                tagRegistry={tagRegistry}
                tagColorMap={tagColorMap}
                ratingsOnly
                onRequestEdit={requestEditTags}
              />
            )}
            {sectionDependencies && isAuthor && (
              <SectionDependencyChip
                sectionKey={sectionKey}
                view={sectionDependencies}
                bookDirection={book?.metadata?.direction || 'ltr'}
                bookFontFamily={book?.settings.titleFontFamily}
                onRemove={(depId) => void onRemoveDependency?.(depId)}
                onEdit={(dep) => onEditDependency?.(dep)}
              />
            )}
          </Box>
          {!multipleChapters && (
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 0.5, minWidth: 0 }}>
              {isAuthor && <Box aria-hidden sx={{ width: 28, minWidth: 28, flexShrink: 0 }} />}
              <Typography
                component="div"
                title={pathLine || undefined}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: '0.7rem',
                  lineHeight: 1.3,
                  color: 'var(--sidebar-text)',
                  opacity: rowSelected ? 0.78 : 0.68,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {pathLine}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    )
  }

  // ── Render group ──

  const renderGroup = (group: ChapterGroup) => {
    const isCollapsed = collapsedChapters.has(group.chapter.id)

    const sectionElements = isCollapsed
      ? null
      : group.entries.map((row) => {
          const keyVal = chapterSectionKey(row.chapter.filename, row.headingBlockId)
          return <Box key={keyVal}>{renderSectionRow(row)}</Box>
        })

    return (
      <React.Fragment key={group.chapter.id}>
        <Box sx={{ mb: multipleChapters ? 0.5 : 0 }}>
          {multipleChapters && (
            <ChapterGroupHeader
              chapterId={group.chapter.id}
              title={group.chapter.title}
              sectionCount={group.entries.length}
              collapsed={isCollapsed}
              onToggle={() => toggleCollapse(group.chapter.id)}
              isAuthor={isAuthor}
              readOnly={readOnly}
              onRename={(newTitle) => onRenameChapter(group.chapter.id, newTitle)}
              onDelete={() => onDeleteChapter(group.chapter.id)}
              onAddBefore={() => void handleAddChapterBefore(group.chapter.id)}
              note={group.chapter.note}
              onOpenNote={(ev) => setNotePopover({ chapterId: group.chapter.id, top: ev.clientY, left: ev.clientX })}
              isRtl={(book?.metadata?.direction || 'ltr') === 'rtl'}
              autoEdit={group.chapter.id === justCreatedChapterId}
              onAutoEditEnd={() => setJustCreatedChapterId(null)}
              fontFamily={book?.settings.titleFontFamily}
            />
          )}
          {sectionElements}
        </Box>
      </React.Fragment>
    )
  }

  const unattachedGroup = chapterGroups.find((g) => g.chapter.id === '__unattached__')
  const groupElements = chapterGroups.filter((g) => g.chapter.id !== '__unattached__').map(renderGroup)

  // Thin uppercase divider that labels a flat-view zone (Placed / Unplaced).
  const renderFlatZoneDivider = (label: string, count: number, mt: number) => (
    <Box
      sx={{
        mt,
        mb: 0.25,
        px: 2,
        py: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        '&::before, &::after': {
          content: '""',
          flex: 1,
          height: '1px',
          bgcolor: 'var(--border-subtle)',
          opacity: 0.6,
        },
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: '0.68rem',
          fontWeight: 600,
          color: 'var(--sidebar-text)',
          opacity: 0.55,
          fontStyle: 'italic',
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
        {count > 0 && (
          <Typography component="span" sx={{ marginInlineStart: '6px', opacity: 0.7 }}>
            ({count})
          </Typography>
        )}
      </Typography>
    </Box>
  )

  // ── Main render ──

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        sx={{
          pl: 0.5,
          pr: 0.5,
          py: 1,
          mb: 0,
          borderBottom: '1px solid var(--border-subtle)',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 0.5,
          position: 'sticky',
          top: -8,
          zIndex: 1,
          bgcolor: 'var(--sidebar-bg)',
        }}
      >
        {(() => {
          // Hebrew labels for RTL books; English otherwise. The user reads the
          // current language naturally — "פרקים" on the right (read first) for
          // Hebrew, "Chapters" on the left for English — without us touching
          // the DOM order, because CSS direction flips the visual layout.
          const direction = book?.metadata?.direction || 'ltr'
          const isRtl = direction === 'rtl'
          const chaptersLabel = isRtl ? 'פרקים' : 'Chapters'
          const sectionsLabel = isRtl ? 'מקטעים' : 'Sections'
          const buttonSx = { textTransform: 'none', px: 1.25, py: 0.25, flex: 1 } as const
          return (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewMode}
              onChange={(_, next) => {
                if (next === 'chapters' || next === 'flat') {
                  setViewMode(next)
                }
              }}
              // MUI applies physical border-radius on :first-of-type / :last-of-type,
              // so in RTL the rounded corners end up in the middle. Override with
              // logical (writing-direction-aware) radius properties.
              sx={{
                flex: 1,
                '& > .MuiToggleButton-root:first-of-type': {
                  borderStartStartRadius: 4,
                  borderEndStartRadius: 4,
                  borderStartEndRadius: 0,
                  borderEndEndRadius: 0,
                },
                '& > .MuiToggleButton-root:last-of-type': {
                  borderStartEndRadius: 4,
                  borderEndEndRadius: 4,
                  borderStartStartRadius: 0,
                  borderEndStartRadius: 0,
                },
              }}
            >
              <ToggleButton value="chapters" sx={buttonSx}>
                {chaptersLabel}
              </ToggleButton>
              <ToggleButton value="flat" sx={buttonSx}>
                {sectionsLabel}
              </ToggleButton>
            </ToggleButtonGroup>
          )
        })()}
        {isAuthor && (
          <>
            {viewMode === 'flat' && onOpenContinuousView && (
              <IconButton
                size="small"
                disabled={readOnly || flatPlacedEntries.length === 0}
                onClick={() => onOpenContinuousView()}
                sx={{
                  color: 'grey.600',
                  '&:hover': { bgcolor: 'grey.100', color: 'grey.800' },
                  ...(readOnly && { opacity: 0.35 }),
                }}
                title="עריכה רציפה של כל המקטעים המשובצים"
              >
                <ViewStream fontSize="small" />
              </IconButton>
            )}
            {tagRegistry && viewMode === 'chapters' && (
              <IconButton
                size="small"
                onClick={(ev) => setFilterAnchor(ev.currentTarget)}
                sx={{
                  color: filterApi.isActive ? 'primary.main' : 'grey.600',
                  '&:hover': { bgcolor: 'grey.100', color: filterApi.isActive ? 'primary.dark' : 'grey.800' },
                }}
                title="סנן לפי דירוג"
              >
                <Badge
                  badgeContent={filterApi.count}
                  color="primary"
                  overlap="circular"
                  variant="dot"
                  invisible={!filterApi.isActive}
                >
                  <FilterList fontSize="small" />
                </Badge>
              </IconButton>
            )}
            <IconButton
              size="small"
              disabled={readOnly || (viewMode === 'flat' && !onFlatSectionsOrderChange)}
              onClick={() => {
                if (viewMode === 'flat') {
                  setOrganizeFlatDialogOpen(true)
                } else {
                  setOrganizeDialogOpen(true)
                }
              }}
              sx={{
                color: 'grey.600',
                '&:hover': { bgcolor: 'grey.100', color: 'grey.800' },
                transition: 'opacity 0.2s ease',
                ...(readOnly && { opacity: 0.35 }),
              }}
              title={readOnly ? undefined : viewMode === 'flat' ? 'Organize sections' : 'Organize chapters'}
            >
              <Reorder fontSize="small" />
            </IconButton>
            {tagRegistry && (
              <IconButton
                size="small"
                onClick={() => setElementsBoardOpen(true)}
                sx={{
                  color: 'grey.600',
                  '&:hover': { bgcolor: 'grey.100', color: 'grey.800' },
                }}
                title="יסודות — מיון מקטעים"
              >
                <GridView fontSize="small" />
              </IconButton>
            )}
            {tagRegistry && (
              <IconButton
                size="small"
                onClick={() => setMatrixOpen(true)}
                sx={{
                  color: 'grey.600',
                  '&:hover': { bgcolor: 'grey.100', color: 'grey.800' },
                }}
                title="יסודות × פרקים — מטריצה"
              >
                <GridOn fontSize="small" />
              </IconButton>
            )}
            {tagRegistry && (
              <IconButton
                size="small"
                onClick={() => setSectionMatrixOpen(true)}
                sx={{
                  color: 'grey.600',
                  '&:hover': { bgcolor: 'grey.100', color: 'grey.800' },
                }}
                title="מקטעים × יסודות — מטריצה"
              >
                <TableRows fontSize="small" />
              </IconButton>
            )}
          </>
        )}
      </Box>

      {viewMode === 'chapters' ? (
        <>
          {groupElements}

          {isAuthor && unattachedGroup && unattachedGroup.entries.length > 0 && (
            <Box sx={{ mb: 0.5, mt: 1, opacity: 0.7 }}>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'var(--sidebar-text)',
                  opacity: 0.6,
                  px: 2,
                  py: 0.5,
                  fontStyle: 'italic',
                }}
              >
                Unattached
              </Typography>
              {unattachedGroup.entries.map((row) => {
                const keyVal = chapterSectionKey(row.chapter.filename, row.headingBlockId)
                return <Box key={keyVal}>{renderSectionRow(row)}</Box>
              })}
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ pt: 0.5 }}>
          {!flatViewAvailable && (
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: 'var(--sidebar-text)',
                opacity: 0.6,
                fontStyle: 'italic',
                px: 2,
                py: 1.5,
              }}
            >
              Enable a flat book structure in Book Settings to start using this view.
            </Typography>
          )}
          {flatViewAvailable && (
            <DndContext
              sensors={flatSensors}
              collisionDetection={detectFlatCollisions}
              onDragStart={handleFlatDragStart}
              onDragOver={handleFlatDragOver}
              onDragEnd={handleFlatDragEnd}
            >
              {/* Placed sections */}
              {(flatUnplacedEntries.length > 0 || activeFlatDragId) &&
                renderFlatZoneDivider(
                  (book?.metadata?.direction || 'ltr') === 'rtl' ? 'משובצים' : 'Placed',
                  flatPlacedEntries.length,
                  0,
                )}
              <DroppableContainer id={FLAT_PLACED_ID}>
                <SortableContext items={draftFlatOrder} strategy={verticalListSortingStrategy}>
                  {flatPlacedEntries.map((row) => {
                    const keyVal = chapterSectionKey(row.chapter.filename, row.headingBlockId)
                    return (
                      <SortableTocRow key={keyVal} id={keyVal} disabled={!flatDragEnabled}>
                        {renderSectionRow(row)}
                      </SortableTocRow>
                    )
                  })}
                </SortableContext>
              </DroppableContainer>
              {flatPlacedEntries.length === 0 && !activeFlatDragId && (
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: 'var(--sidebar-text)',
                    opacity: 0.6,
                    fontStyle: 'italic',
                    px: 2,
                    py: 1.5,
                  }}
                >
                  {flatDragEnabled ? 'Drag a section from Unplaced to place it.' : 'No placed sections yet.'}
                </Typography>
              )}

              {/* Unplaced pool */}
              {(flatUnplacedEntries.length > 0 || activeFlatDragId) &&
                renderFlatZoneDivider(
                  (book?.metadata?.direction || 'ltr') === 'rtl' ? 'לא משובצים' : 'Unplaced',
                  flatUnplacedEntries.length,
                  1.25,
                )}
              <DroppableContainer id={FLAT_UNPLACED_ID}>
                <SortableContext items={draftFlatUnplaced} strategy={verticalListSortingStrategy}>
                  {flatUnplacedEntries.map((row) => {
                    const keyVal = chapterSectionKey(row.chapter.filename, row.headingBlockId)
                    return (
                      <SortableTocRow key={keyVal} id={keyVal} disabled={!flatDragEnabled}>
                        {renderSectionRow(row)}
                      </SortableTocRow>
                    )
                  })}
                </SortableContext>
              </DroppableContainer>

              <DragOverlay>
                {activeFlatEntry ? (
                  <Box sx={{ opacity: 0.95, pointerEvents: 'none' }}>{renderSectionRow(activeFlatEntry)}</Box>
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
        </Box>
      )}

      {isAuthor && organizeDialogOpen && (
        <OrganizeChaptersDialog
          open
          onClose={() => setOrganizeDialogOpen(false)}
          chapterGroups={chapterGroups}
          entryByKey={entryByKey}
          onSave={onSectionDragEnd}
          initialDeps={book?.settings?.chapterSectionDependencies ?? []}
          initialSelectedKey={currentSectionKey}
          bookId={bookId}
          book={book}
          bookFontFamily={book?.settings.titleFontFamily}
          bookDirection={book?.metadata?.direction || 'ltr'}
          tagRegistry={tagRegistry}
          tagColorMap={tagColorMap}
          onSectionTagsPersisted={onSectionTagsPersisted}
          onRegistrySaved={onRegistrySaved}
          onSectionContentSaved={onSectionContentSaved}
        />
      )}

      {isAuthor && elementsBoardOpen && (
        <ElementsBoardDialog
          open
          onClose={() => setElementsBoardOpen(false)}
          entries={chapterSectionEntries}
          entryByKey={entryByKey}
          bookId={bookId}
          bookFontFamily={book?.settings.titleFontFamily}
          bookDirection={book?.metadata?.direction || 'ltr'}
          tagRegistry={tagRegistry}
          tagColorMap={tagColorMap}
          elementSectionOrder={elementSectionOrder}
          onElementSectionOrderChange={onElementSectionOrderChange}
          onSectionTagsPersisted={onSectionTagsPersisted}
          onRegistrySaved={onRegistrySaved}
          book={book}
          onSectionContentSaved={onSectionContentSaved}
          dependencies={sectionDependencies}
          onEditDependency={onEditDependency}
          onRemoveDependency={(depId) => void onRemoveDependency?.(depId)}
        />
      )}

      {isAuthor && matrixOpen && tagRegistry && (
        <ElementChapterMatrixDialog
          open
          onClose={() => setMatrixOpen(false)}
          entries={chapterSectionEntries}
          entryByKey={entryByKey}
          chapterGroups={chapterGroups}
          bookId={bookId}
          bookDirection={book?.metadata?.direction || 'ltr'}
          tagRegistry={tagRegistry}
          onSectionTagsPersisted={onSectionTagsPersisted}
          onRegistrySaved={onRegistrySaved}
          onChaptersSave={onSectionDragEnd}
          book={book}
          onSectionContentSaved={onSectionContentSaved}
        />
      )}

      {isAuthor && sectionMatrixOpen && tagRegistry && (
        <SectionElementMatrixDialog
          open
          onClose={() => setSectionMatrixOpen(false)}
          entries={chapterSectionEntries}
          entryByKey={entryByKey}
          bookId={bookId}
          bookDirection={book?.metadata?.direction || 'ltr'}
          tagRegistry={tagRegistry}
          tagColorMap={tagColorMap}
          onSectionTagsPersisted={onSectionTagsPersisted}
          onFlatSectionsOrderChange={onFlatSectionsOrderChange}
          book={book}
          onSectionContentSaved={onSectionContentSaved}
          dependencies={sectionDependencies}
          onEditDependency={onEditDependency}
          onRemoveDependency={(depId) => void onRemoveDependency?.(depId)}
        />
      )}

      {isAuthor && organizeFlatDialogOpen && onFlatSectionsOrderChange && (
        <OrganizeFlatSectionsDialog
          open
          onClose={() => setOrganizeFlatDialogOpen(false)}
          currentOrder={flatOrder}
          allSectionKeys={allCanonicalKeys}
          entryByKey={entryByKey}
          onSave={onFlatSectionsOrderChange}
          bookFontFamily={book?.settings.titleFontFamily}
          bookDirection={book?.metadata?.direction || 'ltr'}
          onChapterSectionPublish={onChapterSectionPublish}
          chapterSectionPublishBusyKey={chapterSectionPublishBusyKey}
          readOnly={readOnly}
          bookId={bookId}
          book={book}
          onSectionContentSaved={onSectionContentSaved}
          dependencies={sectionDependencies}
          onEditDependency={onEditDependency}
          onRemoveDependency={(depId) => void onRemoveDependency?.(depId)}
          onCreateDependency={onCreateDependency}
        />
      )}

      <Popover
        open={tagPopover !== null}
        onClose={() => setTagPopover(null)}
        anchorReference="anchorPosition"
        anchorPosition={tagPopover ? { top: tagPopover.top, left: tagPopover.left } : undefined}
        anchorOrigin={{
          vertical: 'top',
          horizontal: (book?.metadata?.direction || 'ltr') === 'rtl' ? 'right' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: (book?.metadata?.direction || 'ltr') === 'rtl' ? 'right' : 'left',
        }}
        slotProps={{
          paper: {
            dir: book?.metadata?.direction || 'ltr',
            elevation: 8,
            sx: {
              p: 1.75,
              minWidth: 280,
              maxWidth: 340,
              borderRadius: 2,
              border: '1px solid var(--border-subtle)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        {tagPopover && tagRegistry && (
          <SectionTagEditor
            tagIds={effectiveTagIds(tagPopover.key)}
            tagRegistry={tagRegistry}
            tagColorMap={tagColorMap}
            layout="compact"
            onChange={(next) => {
              void changeTagIds(tagPopover.key, next).catch((err) => {
                logger.warn('Failed to persist section tags from TOC', err)
              })
            }}
            onOpenTagPicker={() => {
              setPickerSectionKey(tagPopover.key)
              setTagPopover(null)
            }}
          />
        )}
      </Popover>

      {(() => {
        const chapter = notePopover
          ? chapterGroups.find((g) => g.chapter.id === notePopover.chapterId)?.chapter
          : undefined
        return (
          <ChapterNotePopover
            open={notePopover !== null && chapter !== undefined}
            anchorPosition={notePopover ? { top: notePopover.top, left: notePopover.left } : null}
            direction={(book?.metadata?.direction || 'ltr') === 'rtl' ? 'rtl' : 'ltr'}
            fontFamily={book?.settings.titleFontFamily}
            chapterTitle={chapter?.title ?? ''}
            note={chapter?.note ?? ''}
            onClose={() => setNotePopover(null)}
            onSave={async (next) => {
              if (!chapter) {
                return
              }
              await onSetChapterNote(chapter.id, next)
            }}
          />
        )
      })()}

      {bookId && (
        <TagManagerDialog
          open={pickerSectionKey !== null}
          onClose={() => setPickerSectionKey(null)}
          bookId={bookId}
          direction={book?.metadata?.direction || 'ltr'}
          mode="select"
          onSelectAndSave={(tagId) => {
            if (pickerSectionKey === null) {
              return
            }
            const current = effectiveTagIds(pickerSectionKey)
            if (!current.includes(tagId)) {
              void changeTagIds(pickerSectionKey, [...current, tagId]).catch((err) => {
                logger.warn('Failed to add section tag from picker', err)
              })
            }
          }}
          onRegistrySaved={onRegistrySaved}
        />
      )}

      <Popover
        open={filterAnchor !== null}
        onClose={() => setFilterAnchor(null)}
        anchorEl={filterAnchor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: (book?.metadata?.direction || 'ltr') === 'rtl' ? 'right' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: (book?.metadata?.direction || 'ltr') === 'rtl' ? 'right' : 'left',
        }}
        slotProps={{
          paper: {
            dir: book?.metadata?.direction || 'ltr',
            elevation: 8,
            sx: {
              p: 1.5,
              minWidth: 300,
              maxWidth: 340,
              borderRadius: 2,
              border: '1px solid var(--border-subtle)',
              backgroundImage: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            },
          },
        }}
      >
        {tagRegistry && (
          <SectionRatingFilterPanel api={filterApi} tagRegistry={tagRegistry} onJumpToGroup={handleJumpToGroup} />
        )}
      </Popover>
    </Box>
  )
}
